import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast, ToastContainer } from "react-toastify";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  Activity,
  BarChart3,
  Building2,
  Clock,
  Code,
  Download,
  FileSpreadsheet,
  FileText,
  Info as InfoIcon,
  KeyRound,
  Maximize2,
  Minimize2,
  TrendingUp,
  User as UserIcon,
  Users,
  Wallet,
} from "lucide-react";

import { API_BASE, MONTHS_MAP, TOASTPROPS } from "../shared/Constants";
import { reportHandledError, reportApiError } from "../utils/reportError";
import {
  PlanInfo,
  UsageHistory,
  UsageInfo,
  UsageSummary,
  OrganizationSummary,
  ModelUsageBody,
} from "../shared/Interfaces";
import {
  buildResumo,
  buildRequestsRows,
  buildResumoSheetRows,
} from "./dashboardExport";
import {
  OrganizationPermission,
  OrganizationRoleValue,
  hasPermission,
} from "../shared/organizationPermissions";
import PaymentModal from "./Payment/PaymentModal";
import { PaymentContext } from "../contexts/PaymentContext";
import { ThemeContext } from "../contexts/ThemeContext";

import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { cn } from "../lib/utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const ALL_MODELS_OPTION = "Todos Modelos";
const ALL_API_KEYS_OPTION = "Todas chaves";
const MODEL_NO_FILTER_LABEL = "Todos os modelos";
const ALL_USERS_OPTION = "Todos usuários";
const MAX_SECONDARY_BARS = 10;

type ChartType =
  | "requests"
  | "costs"
  | "tokens"
  | "model-cost"
  | "api-key-usage"
  | "user-usage";

const fmtCurrency = (value: number) =>
  `R$ ${Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;

export default function BillingDashboard() {
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const { isDarkMode } = useContext(ThemeContext);
  const {
    paymentStage,
    setPaymentStage,
    billingScope,
    setBillingScope,
    activeOrganizationId,
    setActiveOrganizationId,
  } = useContext(PaymentContext);

  const [plan, setPlan] = useState<PlanInfo>();
  const [creditViewForbidden, setCreditViewForbidden] = useState<boolean>(false);
  const [UsageInfoData, setUsageInfoData] = useState<UsageInfo>();
  const [selectedModel, setSelectedModel] = useState<string>(ALL_MODELS_OPTION);
  const [apiKeys, setApiKeys] = useState<string[]>([]);
  const [selectedAPIKey, setSelectedAPIKey] = useState<string>(ALL_API_KEYS_OPTION);
  const [userOptions, setUserOptions] = useState<string[]>([ALL_USERS_OPTION]);
  const [selectedUser, setSelectedUser] = useState<string>(ALL_USERS_OPTION);
  const [usageHistory, setUsageHistory] = useState<UsageHistory>();
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState<boolean>(false);
  const [requestsChartData, setRequestsChartData] = useState<ChartData<"bar">>();
  const [costsChartData, setCostsChartData] = useState<ChartData<"bar">>();
  const [tokensChartData, setTokensChartData] = useState<ChartData<"bar">>();
  const [fullscreenChart, setFullscreenChart] = useState<ChartType | null>(null);

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2023 + 1 },
    (_, i) => 2023 + i
  );
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState<boolean>(false);

  const currentMonth = new Date().getMonth() + 1;
  const [selectedMonth, setSelectedMonth] = useState<string>(MONTHS_MAP[currentMonth]);

  // ───── helpers ─────
  const buildBillingUrl = (
    path: string,
    params: Record<string, string> = {},
    scopeOverride?: "personal" | "organization",
    organizationIdOverride?: string | null
  ) => {
    const scope = scopeOverride ?? billingScope;
    const organizationId = organizationIdOverride ?? activeOrganizationId;
    const search = new URLSearchParams(params);
    if (scope === "organization" && organizationId) {
      search.set("organization_id", organizationId);
    }
    const query = search.toString();
    return `${API_BASE}/api/billing${path}${query ? `?${query}` : ""}`;
  };

  const findMonthIndex = (value: string) => {
    for (const [key, month] of Object.entries(MONTHS_MAP)) {
      if (month === value) return parseInt(key).toString().padStart(2, "0");
    }
    throw new Error("Month not found");
  };

  // ───── orgs ─────
  const fetchOrganizations = useCallback(async () => {
    try {
      setLoadingOrganizations(true);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      const response = await fetch(`${API_BASE}/api/organizations/list`, {
        method: "GET",
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (!response.ok) {
        reportApiError(response, { feature: "dashboard.organizations", method: "GET" });
        toast.error("Erro ao buscar organizações.", TOASTPROPS);
        return;
      }
      const data = await response.json();
      const orgs: OrganizationSummary[] = data.organizations ?? [];
      setOrganizations(orgs);
      if (billingScope === "organization" && activeOrganizationId) {
        const stillExists = orgs.some(
          (org) => org.organization_id === activeOrganizationId
        );
        if (!stillExists) {
          setBillingScope("personal");
          setActiveOrganizationId("");
        }
      }
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "dashboard.organizations" });
      toast.error("Erro ao buscar organizações.", TOASTPROPS);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [
    getAccessTokenSilently,
    billingScope,
    activeOrganizationId,
    setActiveOrganizationId,
    setBillingScope,
  ]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleScopeChange = (value: string) => {
    if (value === "personal") {
      setBillingScope("personal");
      setActiveOrganizationId("");
    } else {
      setBillingScope("organization");
      setActiveOrganizationId(value);
    }
    setSelectedAPIKey(ALL_API_KEYS_OPTION);
    setSelectedModel(ALL_MODELS_OPTION);
    setSelectedUser(ALL_USERS_OPTION);
    setUserOptions([ALL_USERS_OPTION]);
  };

  const scopeSelectValue =
    billingScope === "personal" ? "personal" : activeOrganizationId || "personal";
  const hasOrganizations = organizations.length > 0;
  const isOrganizationScope = billingScope === "organization";

  const currentUserId = useMemo(() => {
    if (!user?.sub) return "";
    const parts = user.sub.split("|");
    return parts[parts.length - 1] || user.sub;
  }, [user]);

  const activeOrganization = useMemo(() => {
    if (!isOrganizationScope || !activeOrganizationId) return undefined;
    return organizations.find((o) => o.organization_id === activeOrganizationId);
  }, [isOrganizationScope, organizations, activeOrganizationId]);

  const memberIdToLabel = useMemo(() => {
    if (!activeOrganization?.members) return {};
    return activeOrganization.members.reduce<Record<string, string>>(
      (acc, member) => {
        if (member.user_id) acc[member.user_id] = member.email || member.user_id;
        return acc;
      },
      {}
    );
  }, [activeOrganization]);

  const currentUserMembership = useMemo(() => {
    if (!activeOrganization || !currentUserId) return undefined;
    return activeOrganization.members?.find((m) => m.user_id === currentUserId);
  }, [activeOrganization, currentUserId]);

  const currentUserRole = currentUserMembership?.role?.toLowerCase() as
    | OrganizationRoleValue
    | undefined;
  const roleDerivedPermissions = useMemo(() => {
    if (!currentUserRole) return [];
    return activeOrganization?.role_permissions?.[currentUserRole] ?? [];
  }, [activeOrganization, currentUserRole]);
  const explicitPermissions = useMemo(
    () => currentUserMembership?.permissions ?? [],
    [currentUserMembership]
  );
  const currentUserPermissions = useMemo(() => {
    if (!roleDerivedPermissions.length && !explicitPermissions.length) return [];
    const merged = new Set<string>();
    roleDerivedPermissions.forEach((p) => merged.add(p));
    explicitPermissions.forEach((p) => merged.add(p));
    return Array.from(merged);
  }, [roleDerivedPermissions, explicitPermissions]);

  const canViewOrgSpend =
    isOrganizationScope &&
    hasPermission(currentUserPermissions, OrganizationPermission.DASHBOARD_VIEW_ORG_SPEND);
  const canFilterUsers = isOrganizationScope && canViewOrgSpend;
  const isOrganizationScopeWithPermission =
    isOrganizationScope && Boolean(activeOrganizationId) && canViewOrgSpend;
  const isOrganizationScopeRestricted =
    isOrganizationScope && Boolean(activeOrganizationId) && !canViewOrgSpend;

  useEffect(() => {
    if (!isOrganizationScope || !canFilterUsers) {
      setSelectedUser((prev) => (prev !== ALL_USERS_OPTION ? ALL_USERS_OPTION : prev));
      setUserOptions((prev) =>
        prev.length === 1 && prev[0] === ALL_USERS_OPTION ? prev : [ALL_USERS_OPTION]
      );
    }
  }, [isOrganizationScope, canFilterUsers]);

  useEffect(() => {
    if (!canFilterUsers) return;
    const members = activeOrganization?.members || [];
    if (!members.length) {
      setUserOptions((prev) =>
        prev.length === 1 && prev[0] === ALL_USERS_OPTION ? prev : [ALL_USERS_OPTION]
      );
      return;
    }
    const uniqueIds = Array.from(
      new Set(members.map((m) => m.user_id).filter((v): v is string => Boolean(v)))
    );
    const sortedIds = [...uniqueIds].sort((a, b) => {
      const labelA = (memberIdToLabel[a] || a).toLowerCase();
      const labelB = (memberIdToLabel[b] || b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
    const normalizedCurrentUserId = currentUserId || "";
    const next = [ALL_USERS_OPTION];
    if (normalizedCurrentUserId) next.push(normalizedCurrentUserId);
    next.push(...sortedIds.filter((id) => id !== normalizedCurrentUserId));
    setUserOptions((prev) => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
      return next;
    });
    if (selectedUser !== ALL_USERS_OPTION && !sortedIds.includes(selectedUser)) {
      setSelectedUser(ALL_USERS_OPTION);
    }
  }, [canFilterUsers, activeOrganization, memberIdToLabel, selectedUser, currentUserId]);

  const getCurrentEntityName = () => {
    if (billingScope === "personal") return "Conta Pessoal";
    if (activeOrganizationId) {
      const org = organizations.find((o) => o.organization_id === activeOrganizationId);
      return org?.name || org?.organization_id || "Organização";
    }
    return "Selecione uma entidade";
  };

  const currentEntityName = getCurrentEntityName();
  const selectedModelDisplay =
    selectedModel === ALL_MODELS_OPTION ? MODEL_NO_FILTER_LABEL : selectedModel;

  const getUserDisplayLabel = (value: string) => {
    if (value === ALL_USERS_OPTION) return ALL_USERS_OPTION;
    return memberIdToLabel[value] || value;
  };

  const selectedUserLabel = getUserDisplayLabel(selectedUser);
  const selectedUserSuffix =
    selectedUser !== ALL_USERS_OPTION ? ` - ${selectedUserLabel}` : "";

  // ───── chart options ─────
  const baseChartTooltip = useMemo(
    () => ({
      backgroundColor: isDarkMode
        ? "rgba(15, 23, 42, 0.95)"
        : "rgba(15, 23, 42, 0.92)",
      titleColor: "#ffffff",
      bodyColor: "#ffffff",
      borderColor: isDarkMode ? "#1e293b" : "#475569",
      borderWidth: 1,
      cornerRadius: 6,
      padding: 10,
    }),
    [isDarkMode]
  );

  const axisTextColor = isDarkMode ? "#94a3b8" : "#697386";
  const titleColor = isDarkMode ? "#e2e8f0" : "#1a1f36";
  const gridColor = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(99,109,131,0.08)";

  const getRequestsOptions = () => {
    const apiKeySuffix = selectedAPIKey !== ALL_API_KEYS_OPTION ? ` - ${selectedAPIKey}` : "";
    const baseTitle =
      selectedModel === ALL_MODELS_OPTION
        ? "Requisições Diárias"
        : `Requisições Diárias - ${selectedModel}`;
    const primarySuffix = `${selectedMonth}/${selectedYear}${apiKeySuffix}${selectedUserSuffix}`;
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            font: { size: 12, family: "Inter, sans-serif" },
            padding: 14,
            usePointStyle: true,
            color: axisTextColor,
          },
        },
        title: {
          display: true,
          text: `${baseTitle} - ${primarySuffix}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          padding: { top: 8, bottom: 16 },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            label: (ti: any) => {
              const value = ti.raw;
              let label = `${ti.dataset.label}: ${value.toLocaleString("pt-BR")} requisições`;
              if (ti.dataset.label === "Horário de Pico") label += " (06:00 - 22:00)";
              else if (ti.dataset.label === "Fora do Horário de Pico") label += " (22:00 - 06:00)";
              return label;
            },
          },
        },
      },
      scales: {
        y: {
          stacked: true,
          title: {
            display: true,
            text: "Requisições",
            font: { size: 12, family: "Inter, sans-serif", weight: "500" as const },
            color: axisTextColor,
          },
          ticks: {
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
            callback: (value: any) => Number(value).toLocaleString("pt-BR"),
          },
          grid: { color: gridColor },
        },
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
          },
        },
      },
    } as any;
  };

  const getCostsOptions = () => {
    const apiKeySuffix = selectedAPIKey !== ALL_API_KEYS_OPTION ? ` - ${selectedAPIKey}` : "";
    const baseTitle =
      selectedModel === ALL_MODELS_OPTION
        ? "Gastos Diários (R$)"
        : `Gastos Diários (R$) - ${selectedModel}`;
    const primarySuffix = `${selectedMonth}/${selectedYear}${apiKeySuffix}${selectedUserSuffix}`;
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            font: { size: 12, family: "Inter, sans-serif" },
            padding: 14,
            usePointStyle: true,
            color: axisTextColor,
          },
        },
        title: {
          display: true,
          text: `${baseTitle} - ${primarySuffix}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          padding: { top: 8, bottom: 16 },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            label: (ti: any) => {
              const value = ti.raw;
              let label = `${ti.dataset.label}: R$ ${parseFloat(value.toFixed(4)).toLocaleString(
                "pt-BR",
                { minimumFractionDigits: 2, maximumFractionDigits: 4 }
              )}`;
              if (ti.dataset.label === "Horário de Pico") label += " (06:00 - 22:00)";
              else if (ti.dataset.label === "Fora do Horário de Pico") label += " (22:00 - 06:00)";
              return label;
            },
          },
        },
      },
      scales: {
        y: {
          stacked: true,
          title: {
            display: true,
            text: "R$",
            font: { size: 12, family: "Inter, sans-serif", weight: "500" as const },
            color: axisTextColor,
          },
          ticks: {
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
            callback: (value: any) =>
              `R$ ${parseFloat(Number(value).toFixed(4)).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
              })}`,
          },
          grid: { color: gridColor },
        },
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
          },
        },
      },
    } as any;
  };

  const getTokensOptions = () => {
    const apiKeySuffix = selectedAPIKey !== ALL_API_KEYS_OPTION ? ` - ${selectedAPIKey}` : "";
    const baseTitle =
      selectedModel === ALL_MODELS_OPTION
        ? "Tokens Diários"
        : `Tokens Diários - ${selectedModel}`;
    const primarySuffix = `${selectedMonth}/${selectedYear}${apiKeySuffix}${selectedUserSuffix}`;
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: {
          position: "bottom" as const,
          labels: {
            font: { size: 12, family: "Inter, sans-serif" },
            padding: 14,
            usePointStyle: true,
            color: axisTextColor,
          },
        },
        title: {
          display: true,
          text: `${baseTitle} - ${primarySuffix}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          padding: { top: 8, bottom: 16 },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            afterBody: (items: any) => {
              if (items.length === 0) return "";
              const dataIndex = items[0].dataIndex;
              const datasets = items[0].chart.data.datasets;
              const inputTokens = datasets[0]?.data[dataIndex] || 0;
              const cachedTokens = datasets[1]?.data[dataIndex] || 0;
              const outputTokens = datasets[2]?.data[dataIndex] || 0;
              const total = inputTokens + cachedTokens + outputTokens;
              return [
                "",
                `Tokens de Input: ${inputTokens.toLocaleString("pt-BR")}`,
                `Tokens em Cache: ${cachedTokens.toLocaleString("pt-BR")}`,
                `Tokens de Output: ${outputTokens.toLocaleString("pt-BR")}`,
                `Total: ${total.toLocaleString("pt-BR")}`,
              ];
            },
            label: () => "",
          },
        },
      },
      scales: {
        y: {
          stacked: true,
          title: {
            display: true,
            text: "Tokens",
            font: { size: 12, family: "Inter, sans-serif", weight: "500" as const },
            color: axisTextColor,
          },
          ticks: {
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
            callback: (value: any) => Number(value).toLocaleString("pt-BR"),
          },
          grid: { color: gridColor },
        },
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            font: { size: 11, family: "Inter, sans-serif" },
            color: axisTextColor,
          },
        },
      },
    } as any;
  };

  const getFullscreenOptions = (options: any) => {
    return {
      ...options,
      plugins: {
        ...options.plugins,
        ...(options.plugins?.legend && {
          legend: {
            ...options.plugins.legend,
            labels: {
              ...options.plugins.legend.labels,
              font: { size: 14, family: "Inter, sans-serif" },
              padding: 18,
            },
          },
        }),
        title: {
          ...options.plugins.title,
          font: { size: 18, family: "Inter, sans-serif", weight: "600" as const },
          ...(options.plugins.title?.padding && {
            padding: { top: 12, bottom: 24 },
          }),
        },
        tooltip: {
          ...options.plugins.tooltip,
          titleFont: { size: 14 },
          bodyFont: { size: 12 },
        },
      },
      scales: {
        ...(options.scales?.y && {
          y: {
            ...options.scales.y,
            ...(options.scales.y?.title && {
              title: {
                ...options.scales.y.title,
                font: { size: 14, family: "Inter, sans-serif", weight: "500" as const },
              },
            }),
            ticks: {
              ...options.scales.y.ticks,
              font: { size: 12, family: "Inter, sans-serif" },
            },
          },
        }),
        ...(options.scales?.x && {
          x: {
            ...options.scales.x,
            ...(options.scales.x?.title && {
              title: {
                ...options.scales.x.title,
                font: { size: 14, family: "Inter, sans-serif", weight: "500" as const },
              },
            }),
            ticks: {
              ...options.scales.x.ticks,
              font: { size: 12, family: "Inter, sans-serif" },
            },
          },
        }),
      },
    };
  };

  // ───── data fetch ─────
  const fetchPlanStatus = async (accessToken: string) => {
    const scopeToUse = isOrganizationScopeWithPermission ? "organization" : "personal";
    const organizationIdToUse = isOrganizationScopeWithPermission
      ? activeOrganizationId
      : undefined;
    if (scopeToUse === "organization" && !organizationIdToUse) {
      setPlan({ credits: 0 });
      setCreditViewForbidden(false);
      return;
    }
    setCreditViewForbidden(false);
    try {
      const endpoint = buildBillingUrl("/status", {}, scopeToUse, organizationIdToUse);
      const response = await fetch(endpoint, {
        method: "GET",
        headers: { Authorization: `Auth ${accessToken}` },
      });
      if (response.ok) {
        setPlan(await response.json());
        return;
      }
      if (response.status === 403) {
        setPlan(undefined);
        setCreditViewForbidden(true);
        return;
      }
      reportApiError(response, { feature: "dashboard.stats", method: "GET" });
      setPlan(undefined);
      const message =
        scopeToUse === "organization"
          ? "Erro ao recuperar status da organização."
          : "Erro ao recuperar status do plano.";
      toast.error(message, TOASTPROPS);
    } catch (error) {
      reportHandledError(error, { category: "api", feature: "dashboard.stats" });
      setPlan(undefined);
      const message =
        scopeToUse === "organization"
          ? "Erro ao recuperar status da organização."
          : "Erro ao recuperar status do plano.";
      toast.error(message, TOASTPROPS);
    }
  };

  useEffect(() => {
    const getUsageHistory = async (accessToken: string) => {
      const month = findMonthIndex(selectedMonth);
      const lastDay = new Date(selectedYear, Number(month), 0).getDate();
      const body: ModelUsageBody = {
        startDate: `${selectedYear}-${month}-01`,
        endDate: `${selectedYear}-${month}-${lastDay}`,
      };
      const shouldFetchOrgUsage =
        isOrganizationScopeWithPermission || isOrganizationScopeRestricted;
      const scopeToUse = shouldFetchOrgUsage ? "organization" : "personal";
      const organizationIdToUse = shouldFetchOrgUsage ? activeOrganizationId : undefined;
      if (scopeToUse === "organization" && !organizationIdToUse) {
        setUsageInfoData(undefined);
        setUsageHistory(undefined);
        setApiKeys([]);
        setUserOptions([ALL_USERS_OPTION]);
        setSelectedUser(ALL_USERS_OPTION);
        return;
      }
      try {
        if (scopeToUse === "organization") {
          if (isOrganizationScopeRestricted) {
            body.userId = currentUserId;
          } else if (canFilterUsers && selectedUser !== ALL_USERS_OPTION) {
            body.userId = selectedUser;
          }
        }
        const endpoint = buildBillingUrl("/usage_history", {}, scopeToUse, organizationIdToUse);
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            Authorization: `Auth ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });
        if (response.ok) {
          let data = await response.json();
          data[ALL_MODELS_OPTION] = data["all"];
          const apiKeysData = data["api_keys"] || {};
          setApiKeys(Object.keys(apiKeysData));
          if (!canFilterUsers && selectedUser !== ALL_USERS_OPTION) {
            setSelectedUser(ALL_USERS_OPTION);
          }
          delete data["all"];
          setUsageInfoData(data);
          const model_keys = Object.keys(data).filter(
            (key) => key !== "api_keys" && key !== "users"
          );
          let model_name = selectedModel;
          if (!model_keys.includes(model_name)) {
            model_name = model_keys[0] || ALL_MODELS_OPTION;
            setSelectedModel(model_name);
          }
          setUsageHistory(data[model_name]);
          setSelectedAPIKey(ALL_API_KEYS_OPTION);
        } else {
          reportApiError(response, { feature: "dashboard.stats", method: "POST" });
          setUsageInfoData(undefined);
          setUsageHistory(undefined);
          setApiKeys([]);
          setUserOptions([ALL_USERS_OPTION]);
          setSelectedUser(ALL_USERS_OPTION);
          const message =
            scopeToUse === "organization"
              ? "Erro ao recuperar histórico de uso da organização."
              : "Erro ao recuperar histórico de uso.";
          toast.error(message, TOASTPROPS);
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "dashboard.stats" });
        setUsageInfoData(undefined);
        setUsageHistory(undefined);
        setApiKeys([]);
        setUserOptions([ALL_USERS_OPTION]);
        setSelectedUser(ALL_USERS_OPTION);
        const message =
          scopeToUse === "organization"
            ? "Erro ao recuperar histórico de uso da organização."
            : "Erro ao recuperar histórico de uso.";
        toast.error(message, TOASTPROPS);
      }
    };

    const fetchStatus = async () => {
      setLoading(true);
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      await fetchPlanStatus(accessToken);
      await getUsageHistory(accessToken);
      setLoading(false);
    };
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMonth,
    selectedYear,
    billingScope,
    activeOrganizationId,
    selectedUser,
    canFilterUsers,
    isOrganizationScopeRestricted,
    isOrganizationScopeWithPermission,
    currentUserId,
    getAccessTokenSilently,
  ]);

  useEffect(() => {
    const fetchStatus = async () => {
      const accessToken = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
      if (!accessToken) return;
      await fetchPlanStatus(accessToken);
    };
    if (paymentStage === "refresh") {
      fetchStatus();
      setPaymentStage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStage, getAccessTokenSilently]);

  useEffect(() => {
    if (selectedUser !== ALL_USERS_OPTION) setSelectedAPIKey(ALL_API_KEYS_OPTION);
  }, [selectedUser]);

  useEffect(() => {
    if (!UsageInfoData) return;
    const availableModels = Object.keys(UsageInfoData).filter(
      (m) => m !== "api_keys" && m !== "users"
    );
    const modelsWithAll = availableModels.includes(ALL_MODELS_OPTION)
      ? availableModels
      : [ALL_MODELS_OPTION, ...availableModels];
    if (selectedModel !== ALL_MODELS_OPTION && !modelsWithAll.includes(selectedModel)) {
      setSelectedModel(ALL_MODELS_OPTION);
    }
  }, [UsageInfoData, selectedModel]);

  const getCurrentUsageData = () => {
    if (!UsageInfoData) return undefined;
    if (selectedAPIKey === ALL_API_KEYS_OPTION) {
      return UsageInfoData[selectedModel] || undefined;
    } else {
      const apiKeysData = (UsageInfoData as any)["api_keys"];
      if (selectedModel !== ALL_MODELS_OPTION) {
        if (apiKeysData && apiKeysData[selectedAPIKey]) {
          return apiKeysData[selectedAPIKey]["models"][selectedModel] || undefined;
        }
      } else {
        return apiKeysData[selectedAPIKey] || undefined;
      }
    }
    return undefined;
  };

  // ───── chart datasets ─────
  useEffect(() => {
    const currentData = getCurrentUsageData();
    const monthNumber = parseInt(findMonthIndex(selectedMonth));
    const daysInMonth = new Date(selectedYear, monthNumber, 0).getDate();
    const allDates = Array.from({ length: daysInMonth }, (_, i) => {
      const day = (i + 1).toString().padStart(2, "0");
      const month = monthNumber.toString().padStart(2, "0");
      return `${day}/${month}`;
    });
    let usageMap: Record<string, UsageSummary> = {};
    if (currentData?.daily_usage?.length) {
      usageMap = currentData.daily_usage.reduce((acc: any, usage: UsageSummary) => {
        acc[usage.short_date] = usage;
        return acc;
      }, {});
    }
    const get = (date: string, key: keyof UsageSummary) => usageMap[date]?.[key] ?? 0;
    const labels = allDates;

    const requestsDatasets = [
      {
        label: "Horário de Pico",
        data: labels.map((d) => Number(get(d, "peak_requests"))),
        backgroundColor: "rgba(99, 102, 241, 0.85)",
        borderColor: "rgba(99, 102, 241, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Noturno",
        data: labels.map((d) => Number(get(d, "offpeak_requests"))),
        backgroundColor: "rgba(34, 197, 94, 0.85)",
        borderColor: "rgba(34, 197, 94, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Batch API",
        data: labels.map((d) => Number(get(d, "batch_api_requests"))),
        backgroundColor: "rgba(168, 85, 247, 0.85)",
        borderColor: "rgba(168, 85, 247, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Flex",
        data: labels.map((d) => Number(get(d, "flex_requests"))),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderColor: "rgba(14, 165, 233, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
    ];
    setRequestsChartData({ labels, datasets: requestsDatasets });

    const costsDatasets = [
      {
        label: "Horário de Pico",
        data: labels.map((d) => Number(get(d, "peak_cost"))),
        backgroundColor: "rgba(245, 101, 101, 0.85)",
        borderColor: "rgba(245, 101, 101, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Noturno",
        data: labels.map((d) => Number(get(d, "offpeak_cost"))),
        backgroundColor: "rgba(56, 178, 172, 0.85)",
        borderColor: "rgba(56, 178, 172, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Batch API",
        data: labels.map((d) => Number(get(d, "batch_api_cost"))),
        backgroundColor: "rgba(251, 146, 60, 0.85)",
        borderColor: "rgba(251, 146, 60, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Flex",
        data: labels.map((d) => Number(get(d, "flex_cost"))),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderColor: "rgba(14, 165, 233, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Extração de Arquivos",
        data: labels.map((d) => Number(get(d, "total_extraction_cost"))),
        backgroundColor: "rgba(32, 162, 80, 0.85)",
        borderColor: "rgba(32, 162, 80, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Tokens em Cache",
        data: labels.map((d) => Number(get(d, "cached_cost"))),
        backgroundColor: "rgba(168, 85, 247, 0.85)",
        borderColor: "rgba(168, 85, 247, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
    ];
    setCostsChartData({ labels, datasets: costsDatasets });

    const tokensDatasets = [
      {
        label: "Tokens de Input",
        data: labels.map((d) => Number(get(d, "input_tokens"))),
        backgroundColor: "rgba(59, 130, 246, 0.85)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Tokens em Cache",
        data: labels.map((d) => Number(get(d, "cached_tokens"))),
        backgroundColor: "rgba(168, 85, 247, 0.85)",
        borderColor: "rgba(168, 85, 247, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
      {
        label: "Tokens de Output",
        data: labels.map((d) => Number(get(d, "output_tokens"))),
        backgroundColor: "rgba(16, 185, 129, 0.85)",
        borderColor: "rgba(16, 185, 129, 1)",
        borderWidth: 1,
        borderRadius: 3,
        stack: "Stack 0",
      },
    ];
    setTokensChartData({ labels, datasets: tokensDatasets });

    setUsageHistory(currentData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UsageInfoData, selectedModel, selectedMonth, selectedYear, selectedAPIKey, selectedUser]);

  // ───── api keys filter ─────
  const getFilteredApiKeys = () => {
    if (isOrganizationScope && selectedUser === ALL_USERS_OPTION) return [ALL_API_KEYS_OPTION];
    if (!UsageInfoData) return [ALL_API_KEYS_OPTION];
    const apiKeysData = (UsageInfoData as any)["api_keys"];
    if (!apiKeysData) return [ALL_API_KEYS_OPTION];
    const filteredKeys = Object.keys(apiKeysData)
      .filter((key) => {
        if (selectedModel === ALL_MODELS_OPTION) return true;
        const keyData = apiKeysData[key];
        return Boolean(keyData?.models?.[selectedModel]);
      })
      .sort((a, b) => a.localeCompare(b));
    if (filteredKeys.length === 0) return [ALL_API_KEYS_OPTION];
    return [ALL_API_KEYS_OPTION, ...filteredKeys];
  };

  useEffect(() => {
    if (UsageInfoData && selectedModel !== ALL_MODELS_OPTION) {
      const filteredKeys = getFilteredApiKeys();
      if (!filteredKeys.includes(selectedAPIKey)) setSelectedAPIKey(ALL_API_KEYS_OPTION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel, UsageInfoData]);

  useEffect(() => {
    if (
      isOrganizationScope &&
      selectedUser === ALL_USERS_OPTION &&
      selectedAPIKey !== ALL_API_KEYS_OPTION
    ) {
      setSelectedAPIKey(ALL_API_KEYS_OPTION);
    }
  }, [isOrganizationScope, selectedUser, selectedAPIKey]);

  // ───── secondary charts ─────
  const modelCostChartData = useMemo<ChartData<"bar"> | undefined>(() => {
    if (!UsageInfoData) return undefined;
    const entries = Object.entries(UsageInfoData)
      .filter(([key]) => key !== "api_keys" && key !== "users" && key !== ALL_MODELS_OPTION)
      .map(([modelName, stats]) => {
        const usage = stats as UsageHistory | undefined;
        return { label: modelName, cost: Number(usage?.total_cost ?? 0) };
      })
      .filter((e) => e.cost > 0)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, MAX_SECONDARY_BARS);
    if (!entries.length) return undefined;
    return {
      labels: entries.map((e) => e.label),
      datasets: [
        {
          label: "Custo (R$)",
          data: entries.map((e) => Number(e.cost.toFixed(4))),
          backgroundColor: "rgba(59, 130, 246, 0.85)",
          borderColor: "rgba(37, 99, 235, 1)",
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 32,
        },
      ],
    };
  }, [UsageInfoData]);

  const modelCostOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Custo por Modelo - ${selectedMonth}/${selectedYear}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            label: (c: any) => `${c.label}: ${fmtCurrency(Number(c.raw ?? 0))}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: axisTextColor,
            font: { size: 11, family: "Inter, sans-serif" },
            callback: (v: any) => fmtCurrency(Number(v)),
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: { color: axisTextColor, font: { size: 11, family: "Inter, sans-serif" } },
          grid: { display: false },
        },
      },
    }),
    [selectedMonth, selectedYear, axisTextColor, titleColor, gridColor, baseChartTooltip]
  );

  type UsageBreakdownEntry = {
    primary: string;
    secondary?: string;
    cost: number;
    chartLabel: string | string[];
  };
  type UsageBreakdownMemo = {
    data?: ChartData<"bar">;
    filteredToCurrentUser?: boolean;
    hasValues: boolean;
    totalEntries: number;
    entries: UsageBreakdownEntry[];
  };

  const usageByApiKeyMemo = useMemo<UsageBreakdownMemo>(() => {
    const base: UsageBreakdownMemo = {
      filteredToCurrentUser: false,
      hasValues: false,
      totalEntries: 0,
      entries: [],
    };
    if (!UsageInfoData) return base;
    const apiKeysData = ((UsageInfoData as any)["api_keys"] ?? {}) as Record<string, UsageHistory>;
    let entries = Object.entries(apiKeysData).map(([keyName, stats]) => ({
      label: keyName,
      cost: Number(stats?.total_cost ?? 0),
      owner_user_id: stats?.owner_user_id,
      owner_email: stats?.owner_email,
    }));
    let filteredToCurrentUser = false;
    if (isOrganizationScope && !canFilterUsers) {
      filteredToCurrentUser = true;
      entries = entries.filter(
        (e) => e.owner_user_id && e.owner_user_id === currentUserId
      );
    }
    entries = entries
      .filter((e) => e.cost > 0)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, MAX_SECONDARY_BARS);
    if (!entries.length) {
      return { ...base, filteredToCurrentUser, entries: [] };
    }
    const detailedEntries: UsageBreakdownEntry[] = entries.map((item) => {
      const primary = item.label;
      const secondary = isOrganizationScope && item.owner_email ? item.owner_email : undefined;
      return {
        primary,
        secondary,
        cost: item.cost,
        chartLabel: secondary ? [primary, secondary] : primary,
      };
    });
    const chartData: ChartData<"bar"> = {
      labels: detailedEntries.map((i) => i.chartLabel),
      datasets: [
        {
          label: "Gasto (R$)",
          data: detailedEntries.map((i) => Number(i.cost.toFixed(4))),
          backgroundColor: "rgba(14, 165, 233, 0.85)",
          borderColor: "rgba(2, 132, 199, 1)",
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 32,
        },
      ],
    };
    return {
      data: chartData,
      filteredToCurrentUser,
      hasValues: true,
      totalEntries: detailedEntries.length,
      entries: detailedEntries,
    };
  }, [UsageInfoData, isOrganizationScope, canFilterUsers, currentUserId]);

  const usageByUserMemo = useMemo<UsageBreakdownMemo>(() => {
    const base: UsageBreakdownMemo = {
      hasValues: false,
      totalEntries: 0,
      entries: [],
    };
    if (!UsageInfoData || !isOrganizationScope || !canFilterUsers) return base;
    const usersData = ((UsageInfoData as any)["users"] ?? {}) as Record<string, UsageHistory>;
    const entries = Object.entries(usersData)
      .map(([userId, stats]) => ({
        label: memberIdToLabel[userId] || userId,
        cost: Number(stats?.total_cost ?? 0),
      }))
      .filter((e) => e.cost > 0)
      .sort((a, b) => b.cost - a.cost)
      .slice(0, MAX_SECONDARY_BARS);
    if (!entries.length) return base;
    const detailedEntries: UsageBreakdownEntry[] = entries.map((i) => ({
      primary: i.label,
      cost: i.cost,
      chartLabel: i.label,
    }));
    const chartData: ChartData<"bar"> = {
      labels: detailedEntries.map((i) => i.chartLabel),
      datasets: [
        {
          label: "Gasto (R$)",
          data: detailedEntries.map((i) => Number(i.cost.toFixed(4))),
          backgroundColor: "rgba(79, 70, 229, 0.85)",
          borderColor: "rgba(67, 56, 202, 1)",
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 32,
        },
      ],
    };
    return {
      data: chartData,
      hasValues: true,
      totalEntries: detailedEntries.length,
      entries: detailedEntries,
    };
  }, [UsageInfoData, isOrganizationScope, canFilterUsers, memberIdToLabel]);

  const apiKeyUsageOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Gasto por Chave de API - ${selectedMonth}/${selectedYear}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            title: (items: any) => {
              const index = items?.[0]?.dataIndex;
              if (index === undefined) return "";
              const entry = usageByApiKeyMemo.entries[index];
              if (!entry) return "";
              const titleLines = [entry.primary];
              if (entry.secondary) titleLines.push(entry.secondary);
              return titleLines;
            },
            label: (c: any) => `Gasto: ${fmtCurrency(Number(c.raw ?? 0))}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: axisTextColor,
            font: { size: 11, family: "Inter, sans-serif" },
            callback: (v: any) => fmtCurrency(Number(v)),
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: { color: axisTextColor, font: { size: 11, family: "Inter, sans-serif" } },
          grid: { display: false },
        },
      },
    }),
    [
      selectedMonth,
      selectedYear,
      usageByApiKeyMemo.entries,
      axisTextColor,
      titleColor,
      gridColor,
      baseChartTooltip,
    ]
  );

  const userUsageOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      layout: { padding: { right: 10, bottom: 10 } },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Gasto por Usuário - ${selectedMonth}/${selectedYear}`,
          font: { size: 14, family: "Inter, sans-serif", weight: "600" as const },
          color: titleColor,
        },
        tooltip: {
          ...baseChartTooltip,
          callbacks: {
            title: (items: any) => {
              const index = items?.[0]?.dataIndex;
              if (index === undefined) return "";
              const entry = usageByUserMemo.entries[index];
              return entry ? entry.primary : "";
            },
            label: (c: any) => `Gasto: ${fmtCurrency(Number(c.raw ?? 0))}`,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            color: axisTextColor,
            font: { size: 11, family: "Inter, sans-serif" },
            callback: (v: any) => fmtCurrency(Number(v)),
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: { color: axisTextColor, font: { size: 11, family: "Inter, sans-serif" } },
          grid: { display: false },
        },
      },
    }),
    [
      selectedMonth,
      selectedYear,
      usageByUserMemo.entries,
      axisTextColor,
      titleColor,
      gridColor,
      baseChartTooltip,
    ]
  );

  // ───── derived ─────
  const formated_credits = (
    plan?.credits !== undefined ? plan.credits.toFixed(2) : "0,00"
  ).replace(".", ",");
  const formated_cost = (
    usageHistory?.total_cost ? usageHistory.total_cost.toFixed(2) : "0,00"
  ).replace(".", ",");
  const shouldHideCredits = creditViewForbidden || isOrganizationScopeRestricted;
  const creditsDisplayValue = shouldHideCredits ? "--" : `R$ ${formated_credits}`;

  const onAPIKeyChange = (value: string) => {
    if (isOrganizationScope && selectedUser === ALL_USERS_OPTION) {
      setSelectedAPIKey(ALL_API_KEYS_OPTION);
      return;
    }
    setSelectedAPIKey(value);
  };

  // ───── chart download/export ─────
  const downloadChart = (format: "png-white" | "png-transparent" | "pdf", chartType: ChartType) => {
    let chartInstance = fullscreenChart
      ? ChartJS.getChart(`fullscreen-${chartType}-chart`)
      : ChartJS.getChart(`${chartType}-chart`);
    if (!chartInstance) {
      chartInstance =
        ChartJS.getChart(`${chartType}-chart`) ||
        ChartJS.getChart(`fullscreen-${chartType}-chart`);
    }
    if (!chartInstance) return;

    const sanitize = (v: string) => v.replace(/[^a-zA-Z0-9]+/g, "_");
    const apiKeyPart = selectedAPIKey !== ALL_API_KEYS_OPTION ? `_${sanitize(selectedAPIKey)}` : "";
    const userPart =
      selectedUser !== ALL_USERS_OPTION ? `_${sanitize(selectedUserLabel)}` : "";
    let fileName = "";
    switch (chartType) {
      case "requests":
        fileName = `requisicoes_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        break;
      case "costs":
        fileName = `gastos_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        break;
      case "tokens":
        fileName = `tokens_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        break;
      case "model-cost":
        fileName = `custo_por_modelo_${selectedMonth}_${selectedYear}`;
        break;
      case "api-key-usage":
        fileName = `gasto_por_chave_api_${selectedMonth}_${selectedYear}`;
        break;
      case "user-usage":
        fileName = `gasto_por_usuario_${selectedMonth}_${selectedYear}`;
        break;
      default:
        fileName = `grafico_${selectedMonth}_${selectedYear}`;
    }

    if (format === "png-white" || format === "png-transparent") {
      if (format === "png-white") {
        const canvas = chartInstance.canvas;
        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        if (tempCtx) {
          tempCtx.fillStyle = "#ffffff";
          tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
          tempCtx.drawImage(canvas, 0, 0);
        }
        const link = document.createElement("a");
        link.download = `${fileName}.png`;
        link.href = tempCanvas.toDataURL("image/png");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const link = document.createElement("a");
        link.download = `${fileName}.png`;
        link.href = chartInstance.toBase64Image("image/png");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else if (format === "pdf") {
      import("jspdf")
        .then((jsPDF) => {
          if (!chartInstance) return;
          const { jsPDF: PDF } = jsPDF;
          const canvas = chartInstance.canvas;
          const tempCanvas = document.createElement("canvas");
          const tempCtx = tempCanvas.getContext("2d");
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          if (tempCtx) {
            tempCtx.fillStyle = "#ffffff";
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            tempCtx.drawImage(canvas, 0, 0);
          }
          const imgData = tempCanvas.toDataURL("image/png");
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;
          const pageWidth = 297;
          const pageHeight = 210;
          const margin = 20;
          const availW = pageWidth - margin * 2;
          const availH = pageHeight - margin * 2;
          const ratio = Math.min(
            availW / (imgWidth * 0.264583),
            availH / (imgHeight * 0.264583)
          );
          const finalW = imgWidth * 0.264583 * ratio;
          const finalH = imgHeight * 0.264583 * ratio;
          const pdf = new PDF("landscape", "mm", "a4");
          pdf.addImage(
            imgData,
            "PNG",
            (pageWidth - finalW) / 2,
            (pageHeight - finalH) / 2,
            finalW,
            finalH
          );
          pdf.save(`${fileName}.pdf`);
        })
        .catch(() => {});
    }
  };

  const exportData = (format: "csv" | "xlsx" | "json", chartType: ChartType) => {
    const sanitize = (v: string) => v.replace(/[^a-zA-Z0-9]+/g, "_");
    const apiKeyPart = selectedAPIKey !== ALL_API_KEYS_OPTION ? `_${sanitize(selectedAPIKey)}` : "";
    const userPart =
      selectedUser !== ALL_USERS_OPTION ? `_${sanitize(selectedUserLabel)}` : "";

    let fileName = "";
    let data: any[] = [];
    let headers: string[] = [];

    switch (chartType) {
      case "requests":
        fileName = `requisicoes_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        headers = ["Data", "Horário de Pico", "Fora do Horário de Pico", "Batch API", "Flex", "Total"];
        if (requestsChartData?.labels && requestsChartData?.datasets) {
          data = requestsChartData.labels.map((label, i) => ({
            Data: label,
            "Horário de Pico": requestsChartData.datasets[0]?.data[i] || 0,
            "Fora do Horário de Pico": requestsChartData.datasets[1]?.data[i] || 0,
            "Batch API": requestsChartData.datasets[2]?.data[i] || 0,
            Flex: requestsChartData.datasets[3]?.data[i] || 0,
            Total:
              Number(requestsChartData.datasets[0]?.data[i] || 0) +
              Number(requestsChartData.datasets[1]?.data[i] || 0) +
              Number(requestsChartData.datasets[2]?.data[i] || 0) +
              Number(requestsChartData.datasets[3]?.data[i] || 0),
          }));
        }
        break;
      case "costs":
        fileName = `gastos_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        headers = [
          "Data",
          "Horário de Pico (R$)",
          "Fora do Horário de Pico (R$)",
          "Batch API (R$)",
          "Flex (R$)",
          "Extração de Arquivos (R$)",
          "Tokens em Cache (R$)",
          "Total (R$)",
        ];
        if (costsChartData?.labels && costsChartData?.datasets) {
          data = costsChartData.labels.map((label, i) => ({
            Data: label,
            "Horário de Pico (R$)": Number(costsChartData.datasets[0]?.data[i] || 0).toFixed(4),
            "Fora do Horário de Pico (R$)": Number(costsChartData.datasets[1]?.data[i] || 0).toFixed(4),
            "Batch API (R$)": Number(costsChartData.datasets[2]?.data[i] || 0).toFixed(4),
            "Flex (R$)": Number(costsChartData.datasets[3]?.data[i] || 0).toFixed(4),
            "Extração de Arquivos (R$)": Number(costsChartData.datasets[4]?.data[i] || 0).toFixed(4),
            "Tokens em Cache (R$)": Number(costsChartData.datasets[5]?.data[i] || 0).toFixed(4),
            "Total (R$)": (
              Number(costsChartData.datasets[0]?.data[i] || 0) +
              Number(costsChartData.datasets[1]?.data[i] || 0) +
              Number(costsChartData.datasets[2]?.data[i] || 0) +
              Number(costsChartData.datasets[3]?.data[i] || 0) +
              Number(costsChartData.datasets[4]?.data[i] || 0) +
              Number(costsChartData.datasets[5]?.data[i] || 0)
            ).toFixed(4),
          }));
        }
        break;
      case "tokens":
        fileName = `tokens_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;
        headers = ["Data", "Tokens de Input", "Tokens em Cache", "Tokens de Output", "Total"];
        if (tokensChartData?.labels && tokensChartData?.datasets) {
          data = tokensChartData.labels.map((label, i) => ({
            Data: label,
            "Tokens de Input": tokensChartData.datasets[0]?.data[i] || 0,
            "Tokens em Cache": tokensChartData.datasets[1]?.data[i] || 0,
            "Tokens de Output": tokensChartData.datasets[2]?.data[i] || 0,
            Total:
              Number(tokensChartData.datasets[0]?.data[i] || 0) +
              Number(tokensChartData.datasets[1]?.data[i] || 0) +
              Number(tokensChartData.datasets[2]?.data[i] || 0),
          }));
        }
        break;
      case "model-cost":
        fileName = `custo_por_modelo_${selectedMonth}_${selectedYear}`;
        headers = ["Modelo", "Custo (R$)"];
        if (modelCostChartData?.labels && modelCostChartData?.datasets) {
          data = modelCostChartData.labels.map((label, i) => ({
            Modelo: label,
            "Custo (R$)": Number(modelCostChartData.datasets[0]?.data[i] || 0).toFixed(4),
          }));
        }
        break;
      case "api-key-usage":
        fileName = `gasto_por_chave_api_${selectedMonth}_${selectedYear}`;
        headers = ["Chave de API", "Gasto (R$)"];
        if (usageByApiKeyMemo.data?.labels && usageByApiKeyMemo.data?.datasets) {
          data = usageByApiKeyMemo.data.labels.map((label, i) => ({
            "Chave de API": Array.isArray(label) ? label.join(" - ") : label,
            "Gasto (R$)": Number(usageByApiKeyMemo.data!.datasets[0]?.data[i] || 0).toFixed(4),
          }));
        }
        break;
      case "user-usage":
        fileName = `gasto_por_usuario_${selectedMonth}_${selectedYear}`;
        headers = ["Usuário", "Gasto (R$)"];
        if (usageByUserMemo.data?.labels && usageByUserMemo.data?.datasets) {
          data = usageByUserMemo.data.labels.map((label, i) => ({
            Usuário: label,
            "Gasto (R$)": Number(usageByUserMemo.data!.datasets[0]?.data[i] || 0).toFixed(4),
          }));
        }
        break;
      default:
        fileName = `dados_${selectedMonth}_${selectedYear}`;
    }

    if (data.length === 0) {
      toast.error("Não há dados para exportar.", TOASTPROPS);
      return;
    }

    if (format === "csv") {
      const csvContent = [
        headers.join(";"),
        ...data.map((row) => headers.map((h) => row[h] ?? "").join(";")),
      ].join("\n");
      const blob = new Blob(["﻿" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else if (format === "xlsx") {
      import("xlsx")
        .then((XLSX) => {
          const ws = XLSX.utils.json_to_sheet(data, { header: headers });
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Dados");
          XLSX.writeFile(wb, `${fileName}.xlsx`);
        })
        .catch((error) => {
          reportHandledError(error, { category: "export", feature: "dashboard.export" });
          toast.error("Erro ao exportar para XLSX.", TOASTPROPS);
        });
    } else if (format === "json") {
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  };

  const exportAllData = (format: "csv" | "xlsx" | "json") => {
    const sanitize = (v: string) => v.replace(/[^a-zA-Z0-9]+/g, "_");
    const apiKeyPart = selectedAPIKey !== ALL_API_KEYS_OPTION ? `_${sanitize(selectedAPIKey)}` : "";
    const userPart =
      selectedUser !== ALL_USERS_OPTION ? `_${sanitize(selectedUserLabel)}` : "";
    const fileName = `dados_completos_${sanitize(selectedModelDisplay)}_${selectedMonth}_${selectedYear}${apiKeyPart}${userPart}`;

    const allData: any = {
      metadata: {
        periodo: `${selectedMonth}/${selectedYear}`,
        modelo: selectedModelDisplay,
        chave_api: selectedAPIKey === ALL_API_KEYS_OPTION ? "Todas" : selectedAPIKey,
        usuario: selectedUser === ALL_USERS_OPTION ? "Todos" : selectedUserLabel,
        exportado_em: new Date().toISOString(),
      },
      resumo: buildResumo(usageHistory, plan?.credits ?? 0),
      requisicoes_diarias: [] as any[],
      gastos_diarios: [] as any[],
      tokens_diarios: [] as any[],
      custo_por_modelo: [] as any[],
      gasto_por_chave_api: [] as any[],
      gasto_por_usuario: [] as any[],
    };

    allData.requisicoes_diarias = buildRequestsRows(requestsChartData);
    if (costsChartData?.labels && costsChartData?.datasets) {
      allData.gastos_diarios = costsChartData.labels.map((label, i) => ({
        data: label,
        horario_pico: Number(costsChartData.datasets[0]?.data[i] || 0),
        fora_horario_pico: Number(costsChartData.datasets[1]?.data[i] || 0),
        batch_api: Number(costsChartData.datasets[2]?.data[i] || 0),
        extracao_arquivos: Number(costsChartData.datasets[3]?.data[i] || 0),
        tokens_em_cache: Number(costsChartData.datasets[4]?.data[i] || 0),
        total:
          Number(costsChartData.datasets[0]?.data[i] || 0) +
          Number(costsChartData.datasets[1]?.data[i] || 0) +
          Number(costsChartData.datasets[2]?.data[i] || 0) +
          Number(costsChartData.datasets[3]?.data[i] || 0) +
          Number(costsChartData.datasets[4]?.data[i] || 0),
      }));
    }
    if (tokensChartData?.labels && tokensChartData?.datasets) {
      allData.tokens_diarios = tokensChartData.labels.map((label, i) => ({
        data: label,
        tokens_input: tokensChartData.datasets[0]?.data[i] || 0,
        tokens_em_cache: tokensChartData.datasets[1]?.data[i] || 0,
        tokens_output: tokensChartData.datasets[2]?.data[i] || 0,
        total:
          Number(tokensChartData.datasets[0]?.data[i] || 0) +
          Number(tokensChartData.datasets[1]?.data[i] || 0) +
          Number(tokensChartData.datasets[2]?.data[i] || 0),
      }));
    }
    if (modelCostChartData?.labels && modelCostChartData?.datasets) {
      allData.custo_por_modelo = modelCostChartData.labels.map((label, i) => ({
        modelo: label,
        custo: Number(modelCostChartData.datasets[0]?.data[i] || 0),
      }));
    }
    if (usageByApiKeyMemo.data?.labels && usageByApiKeyMemo.data?.datasets) {
      allData.gasto_por_chave_api = usageByApiKeyMemo.data.labels.map((label, i) => ({
        chave_api: Array.isArray(label) ? label.join(" - ") : label,
        gasto: Number(usageByApiKeyMemo.data!.datasets[0]?.data[i] || 0),
      }));
    }
    if (usageByUserMemo.data?.labels && usageByUserMemo.data?.datasets) {
      allData.gasto_por_usuario = usageByUserMemo.data.labels.map((label, i) => ({
        usuario: label,
        gasto: Number(usageByUserMemo.data!.datasets[0]?.data[i] || 0),
      }));
    }

    if (format === "json") {
      const blob = new Blob([JSON.stringify(allData, null, 2)], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${fileName}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } else {
      import("xlsx")
        .then((XLSX) => {
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(
            wb,
            XLSX.utils.json_to_sheet(buildResumoSheetRows(allData)),
            "Resumo"
          );
          if (allData.requisicoes_diarias.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.requisicoes_diarias),
              "Requisições"
            );
          if (allData.gastos_diarios.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.gastos_diarios),
              "Gastos"
            );
          if (allData.tokens_diarios.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.tokens_diarios),
              "Tokens"
            );
          if (allData.custo_por_modelo.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.custo_por_modelo),
              "Custo por Modelo"
            );
          if (allData.gasto_por_chave_api.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.gasto_por_chave_api),
              "Por Chave API"
            );
          if (allData.gasto_por_usuario.length)
            XLSX.utils.book_append_sheet(
              wb,
              XLSX.utils.json_to_sheet(allData.gasto_por_usuario),
              "Por Usuário"
            );
          if (format === "xlsx") XLSX.writeFile(wb, `${fileName}.xlsx`);
          else XLSX.writeFile(wb, `${fileName}.csv`, { bookType: "csv" });
        })
        .catch((error) => {
          reportHandledError(error, { category: "export", feature: "dashboard.export" });
          toast.error("Erro ao exportar.", TOASTPROPS);
        });
    }
  };

  const requestsOptions = getRequestsOptions();
  const costsOptions = getCostsOptions();
  const tokensOptions = getTokensOptions();
  const requestsChartKey = `requests-${selectedModel}-${selectedAPIKey}-${selectedUser}-${selectedMonth}-${selectedYear}-${requestsChartData?.labels?.length ?? 0}`;
  const costsChartKey = `costs-${selectedModel}-${selectedAPIKey}-${selectedUser}-${selectedMonth}-${selectedYear}-${costsChartData?.labels?.length ?? 0}`;
  const tokensChartKey = `tokens-${selectedModel}-${selectedAPIKey}-${selectedUser}-${selectedMonth}-${selectedYear}-${tokensChartData?.labels?.length ?? 0}`;
  const filteredApiKeys = getFilteredApiKeys();
  const showWarning = apiKeys.includes("Outras chaves");
  const modelCostChartKey = `model-cost-${selectedMonth}-${selectedYear}-${modelCostChartData?.labels?.length ?? 0}`;
  const usageByApiKeyChartKey = `usage-api-${selectedMonth}-${selectedYear}-${usageByApiKeyMemo.totalEntries}-${usageByApiKeyMemo.filteredToCurrentUser ? "self" : "all"}`;
  const usageByUserChartKey = `usage-user-${selectedMonth}-${selectedYear}-${usageByUserMemo.totalEntries}`;

  // ───── shared subcomponents ─────
  const KpiCard = ({
    label,
    value,
    icon,
    sub,
    info,
  }: {
    label: string;
    value: React.ReactNode;
    icon: React.ReactNode;
    sub?: string;
    info?: string;
  }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </p>
              {info && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InfoIcon className="h-3 w-3 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>{info}</TooltipContent>
                </Tooltip>
              )}
            </div>
            {loading ? (
              <Skeleton className="mt-1.5 h-7 w-32" />
            ) : (
              <p className="mt-1 text-[22px] font-semibold tracking-tight text-foreground font-mono">
                {value}
              </p>
            )}
            {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
          </div>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-foreground">
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ChartCard = ({
    chartId,
    chartType,
    title: chartTitle,
    icon,
    subtitle,
    info,
    chartData,
    options,
    chartKey,
    note,
    emptyMessage,
  }: {
    chartId: string;
    chartType: ChartType;
    title: string;
    icon: React.ReactNode;
    subtitle?: string;
    info?: string;
    chartData: ChartData<"bar"> | undefined;
    options: any;
    chartKey: string;
    note?: string;
    emptyMessage?: string;
  }) => (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {icon}
          <p className="truncate text-[13px] font-semibold text-foreground">{chartTitle}</p>
          {subtitle && (
            <span className="truncate text-[12px] text-muted-foreground">· {subtitle}</span>
          )}
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">{info}</TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setFullscreenChart(chartType)}
            aria-label="Expandir gráfico"
            title="Expandir gráfico"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Baixar gráfico" title="Baixar gráfico">
                <Download className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => downloadChart("png-white", chartType)}>
                <Download className="h-4 w-4" />
                PNG (Fundo Branco)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadChart("png-transparent", chartType)}>
                <Download className="h-4 w-4" />
                PNG (Transparente)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadChart("pdf", chartType)}>
                <FileText className="h-4 w-4" />
                PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportData("csv", chartType)}>
                <FileSpreadsheet className="h-4 w-4" />
                CSV (Dados)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("xlsx", chartType)}>
                <FileSpreadsheet className="h-4 w-4" />
                Excel (XLSX)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportData("json", chartType)}>
                <Code className="h-4 w-4" />
                JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="relative h-[380px] p-4">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : chartData ? (
          <Bar id={chartId} key={chartKey} options={options} data={chartData} />
        ) : (
          <div className="flex h-full items-center justify-center text-[13px] text-muted-foreground">
            {emptyMessage ?? "Sem dados no período selecionado."}
          </div>
        )}
      </div>
      {!loading && note && chartData && (
        <p className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">{note}</p>
      )}
    </Card>
  );

  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* Entity Badge */}
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <div
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-md border border-border",
                isOrganizationScope ? "bg-primary/10 text-primary" : "bg-muted text-foreground"
              )}
            >
              {isOrganizationScope ? (
                <Building2 className="h-4 w-4" />
              ) : (
                <UserIcon className="h-4 w-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {isOrganizationScope ? "Organização ativa" : "Conta pessoal"}
              </p>
              <p className="truncate text-[14px] font-semibold text-foreground">
                {currentEntityName}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Scope selector */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[12.5px] font-medium text-muted-foreground">
            Visualizar gastos de:
          </span>
          {loadingOrganizations ? (
            <Skeleton className="h-9 w-64" />
          ) : (
            <Select value={scopeSelectValue} onValueChange={handleScopeChange}>
              <SelectTrigger className="w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">
                  <span className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4" />
                    Minha conta pessoal
                  </span>
                </SelectItem>
                {organizations.map((org) => (
                  <SelectItem key={org.organization_id} value={org.organization_id}>
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      {org.name || org.organization_id}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {isOrganizationScopeRestricted && (
          <p className="text-[13px] text-muted-foreground">
            Você não tem permissão para ver os gastos consolidados desta organização; estamos
            mostrando apenas o seu uso pessoal dentro dela.
          </p>
        )}
        {!loadingOrganizations && !hasOrganizations && (
          <p className="text-[13px] text-muted-foreground">
            Crie uma organização para compartilhar créditos e visualizar gastos consolidados da
            equipe.
          </p>
        )}

        {/* Filters */}
        <Card>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Filtros
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" disabled={loading} className="gap-2">
                  <Download className="h-3.5 w-3.5" />
                  Exportar Tudo
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem onClick={() => exportAllData("xlsx")}>
                  <FileSpreadsheet className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[13px] font-medium">Excel (XLSX)</span>
                    <span className="text-[11px] text-muted-foreground">
                      Múltiplas abas com todos os dados
                    </span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportAllData("csv")}>
                  <FileSpreadsheet className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[13px] font-medium">CSV</span>
                    <span className="text-[11px] text-muted-foreground">Formato tabular simples</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportAllData("json")}>
                  <Code className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[13px] font-medium">JSON</span>
                    <span className="text-[11px] text-muted-foreground">
                      Estrutura completa para integração
                    </span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-wrap gap-3 p-4">
            <div className="flex w-full flex-col gap-1.5 sm:w-52">
              <label className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Modelo
              </label>
              <Select
                value={selectedModel}
                onValueChange={(v) => setSelectedModel(v)}
              >
                <SelectTrigger>
                  <SelectValue>
                    {selectedModel === ALL_MODELS_OPTION ? "Todos os modelos" : selectedModel}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {UsageInfoData &&
                    Object.keys(UsageInfoData)
                      .filter((m) => m !== "api_keys" && m !== "users")
                      .sort((a, b) => {
                        if (a === ALL_MODELS_OPTION) return -1;
                        if (b === ALL_MODELS_OPTION) return 1;
                        return a.localeCompare(b);
                      })
                      .map((m) => (
                        <SelectItem key={m} value={m}>
                          {m === ALL_MODELS_OPTION ? "Todos os modelos" : m}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>
            {isOrganizationScope && (
              <div className="flex w-full flex-col gap-1.5 sm:w-52">
                <label className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Usuário
                </label>
                <Select
                  value={selectedUser}
                  onValueChange={(v) => setSelectedUser(v)}
                  disabled={!canFilterUsers || userOptions.length <= 1}
                >
                  <SelectTrigger>
                    <SelectValue>{getUserDisplayLabel(selectedUser)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {userOptions.map((u) => (
                      <SelectItem key={u} value={u}>
                        {getUserDisplayLabel(u)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex w-full flex-col gap-1.5 sm:w-52">
              <label className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                API Key
              </label>
              <Select
                value={selectedAPIKey}
                onValueChange={(v) => onAPIKeyChange(v)}
                disabled={filteredApiKeys.length <= 1}
              >
                <SelectTrigger>
                  <SelectValue>
                    {selectedAPIKey === ALL_API_KEYS_OPTION ? "Todas as chaves" : selectedAPIKey}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {filteredApiKeys.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k === ALL_API_KEYS_OPTION ? "Todas as chaves" : k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-full flex-col gap-1.5 sm:w-40">
              <label className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Período
              </label>
              <Select
                value={selectedMonth}
                onValueChange={(v) => setSelectedMonth(v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(MONTHS_MAP).map((mk) => (
                    <SelectItem key={mk} value={MONTHS_MAP[parseInt(mk)]}>
                      {MONTHS_MAP[parseInt(mk)]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-full flex-col gap-1.5 sm:w-32">
              <label className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Ano
              </label>
              <Select
                value={String(selectedYear)}
                onValueChange={(v) => setSelectedYear(parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {showWarning && (
          <Card className="border-warning/40 bg-warning/5">
            <CardContent className="p-3 text-[13px] text-foreground/90">
              <strong className="text-foreground">Atenção:</strong> Os dados anteriores a abril de
              2025 não estão segregados por chave de API e se encontram na chave "Outras chaves".
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Saldo Disponível"
            value={creditsDisplayValue}
            icon={<Wallet className="h-4 w-4" />}
            sub="Créditos em conta"
            info={
              shouldHideCredits
                ? "Você não tem permissão para visualizar o saldo de créditos."
                : undefined
            }
          />
          <KpiCard
            label="Gastos do Período"
            value={`R$ ${formated_cost}`}
            icon={<TrendingUp className="h-4 w-4" />}
            sub={`${selectedMonth}/${selectedYear}`}
          />
          <KpiCard
            label="Total de Requisições"
            value={
              usageHistory?.total_requests?.toLocaleString("pt-BR") ?? "0"
            }
            icon={<Activity className="h-4 w-4" />}
            sub="No período"
          />
          <KpiCard
            label="Custo Médio por Requisição"
            value={
              usageHistory?.total_requests
                ? `R$ ${(usageHistory.total_cost / usageHistory.total_requests)
                    .toFixed(4)
                    .replace(".", ",")}`
                : "R$ 0,0000"
            }
            icon={<BarChart3 className="h-4 w-4" />}
            sub="Média calculada"
          />
        </div>

        {/* Time breakdown */}
        <Card>
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="text-[13px]">Detalhamento por modalidade</CardTitle>
            <CardDescription>
              Gastos divididos por horário e modalidade no período.
            </CardDescription>
          </CardHeader>
          <div className="grid grid-cols-2 divide-x divide-border md:grid-cols-5">
            {[
              {
                label: "Horário de Pico",
                info: "06:00 às 22:00 (preços mais altos)",
                value:
                  usageHistory?.total_peak_cost?.toFixed(2).replace(".", ",") ?? "0,00",
              },
              {
                label: "Noturno",
                info: "22:00 às 06:00 (preços reduzidos)",
                value:
                  usageHistory?.total_offpeak_cost?.toFixed(2).replace(".", ",") ?? "0,00",
              },
              {
                label: "Batch API",
                value:
                  usageHistory?.total_batch_api_cost?.toFixed(2).replace(".", ",") ?? "0,00",
              },
              {
                label: "Flex",
                value:
                  usageHistory?.total_flex_cost?.toFixed(2).replace(".", ",") ?? "0,00",
              },
              {
                label: "Extração",
                value:
                  usageHistory?.total_extraction_cost?.toFixed(2).replace(".", ",") ?? "0,00",
              },
            ].map((item) => (
              <div key={item.label} className="space-y-1 p-4">
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {item.label}
                  </p>
                  {item.info && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-3 w-3 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>{item.info}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {loading ? (
                  <Skeleton className="h-5 w-20" />
                ) : (
                  <p className="font-mono text-[14.5px] font-semibold text-foreground">
                    R$ {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Charts grid */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <ChartCard
            chartId="costs-chart"
            chartType="costs"
            title="Análise de Gastos"
            icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
            subtitle={`${selectedMonth}/${selectedYear}${
              selectedModel !== ALL_MODELS_OPTION ? ` · ${selectedModel}` : ""
            }${selectedAPIKey !== ALL_API_KEYS_OPTION ? ` · ${selectedAPIKey}` : ""}${selectedUserSuffix}`}
            info="Horário de Pico: 06:00-22:00 (preços mais altos) | Noturno: 22:00-06:00 (preços reduzidos)"
            chartData={costsChartData}
            options={costsOptions}
            chartKey={costsChartKey}
          />
          <ChartCard
            chartId="requests-chart"
            chartType="requests"
            title="Análise de Requisições"
            icon={<Activity className="h-4 w-4 text-muted-foreground" />}
            subtitle={`${selectedMonth}/${selectedYear}${
              selectedModel !== ALL_MODELS_OPTION ? ` · ${selectedModel}` : ""
            }${selectedAPIKey !== ALL_API_KEYS_OPTION ? ` · ${selectedAPIKey}` : ""}${selectedUserSuffix}`}
            info="Horário de Pico: 06:00-22:00 | Noturno: 22:00-06:00"
            chartData={requestsChartData}
            options={requestsOptions}
            chartKey={requestsChartKey}
          />
          <ChartCard
            chartId="tokens-chart"
            chartType="tokens"
            title="Tokens Diários"
            icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
            subtitle={`${selectedMonth}/${selectedYear}${
              selectedModel !== ALL_MODELS_OPTION ? ` · ${selectedModel}` : ""
            }`}
            info="Tokens de input, output e total consumidos por dia"
            chartData={tokensChartData}
            options={tokensOptions}
            chartKey={tokensChartKey}
          />
          <ChartCard
            chartId="model-cost-chart"
            chartType="model-cost"
            title="Custo por Modelo"
            icon={<BarChart3 className="h-4 w-4 text-muted-foreground" />}
            subtitle={`${selectedMonth}/${selectedYear}`}
            chartData={modelCostChartData}
            options={modelCostOptions}
            chartKey={modelCostChartKey}
            note="Dados agregados por modelo, sem filtros adicionais."
            emptyMessage="Sem dados de custo por modelo no período selecionado."
          />
          <ChartCard
            chartId="api-key-usage-chart"
            chartType="api-key-usage"
            title="Gasto por Chave de API"
            icon={<KeyRound className="h-4 w-4 text-muted-foreground" />}
            subtitle={`${selectedMonth}/${selectedYear} · Por chave`}
            chartData={usageByApiKeyMemo.data}
            options={apiKeyUsageOptions}
            chartKey={usageByApiKeyChartKey}
            note={
              usageByApiKeyMemo.filteredToCurrentUser
                ? "Exibindo somente suas chaves (top 10 por gasto)."
                : "Exibindo top 10 chaves por gasto."
            }
            emptyMessage={
              usageByApiKeyMemo.filteredToCurrentUser
                ? "Sem gastos associados às suas chaves no período selecionado."
                : "Sem dados de gasto por chave no período selecionado."
            }
          />
          {isOrganizationScope && canFilterUsers && (
            <ChartCard
              chartId="user-usage-chart"
              chartType="user-usage"
              title="Gasto por Usuário"
              icon={<Users className="h-4 w-4 text-muted-foreground" />}
              subtitle={`${selectedMonth}/${selectedYear} · Por e-mail`}
              chartData={usageByUserMemo.data}
              options={userUsageOptions}
              chartKey={usageByUserChartKey}
              note="Exibindo top 10 usuários por gasto."
              emptyMessage="Não há gastos por usuário no período selecionado."
            />
          )}
        </div>

        {/* Fullscreen chart dialog */}
        <Dialog open={fullscreenChart !== null} onOpenChange={(o) => !o && setFullscreenChart(null)}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] w-full sm:!max-w-[95vw] flex flex-col p-0">
            <DialogHeader className="flex-row items-center justify-between gap-2 border-b border-border px-5 py-3">
              <DialogTitle className="flex items-center gap-2 text-[15px]">
                {fullscreenChart === "requests" && <Activity className="h-4 w-4" />}
                {fullscreenChart === "costs" && <TrendingUp className="h-4 w-4" />}
                {fullscreenChart === "tokens" && <BarChart3 className="h-4 w-4" />}
                {fullscreenChart === "model-cost" && <BarChart3 className="h-4 w-4" />}
                {fullscreenChart === "api-key-usage" && <KeyRound className="h-4 w-4" />}
                {fullscreenChart === "user-usage" && <Users className="h-4 w-4" />}
                <span>
                  {fullscreenChart === "requests" && "Requisições Diárias"}
                  {fullscreenChart === "costs" && "Gastos Diários"}
                  {fullscreenChart === "tokens" && "Tokens Diários"}
                  {fullscreenChart === "model-cost" && "Custo por Modelo"}
                  {fullscreenChart === "api-key-usage" && "Gasto por Chave de API"}
                  {fullscreenChart === "user-usage" && "Gasto por Usuário"}
                  {[
                    fullscreenChart === "requests" || fullscreenChart === "costs" || fullscreenChart === "tokens"
                      ? selectedModel !== ALL_MODELS_OPTION
                        ? ` - ${selectedModel}`
                        : ""
                      : "",
                    fullscreenChart === "requests" || fullscreenChart === "costs" || fullscreenChart === "tokens"
                      ? selectedAPIKey !== ALL_API_KEYS_OPTION
                        ? ` - ${selectedAPIKey}`
                        : ""
                      : "",
                    fullscreenChart === "requests" || fullscreenChart === "costs" || fullscreenChart === "tokens"
                      ? selectedUserSuffix
                      : "",
                    " - " + selectedMonth + "/" + selectedYear,
                  ].join("")}
                </span>
              </DialogTitle>
              <div className="flex items-center gap-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Baixar gráfico">
                      <Download className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem
                      onClick={() => downloadChart("png-white", fullscreenChart!)}
                    >
                      <Download className="h-4 w-4" />
                      PNG (Fundo Branco)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => downloadChart("png-transparent", fullscreenChart!)}
                    >
                      <Download className="h-4 w-4" />
                      PNG (Transparente)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => downloadChart("pdf", fullscreenChart!)}>
                      <FileText className="h-4 w-4" />
                      PDF
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => exportData("csv", fullscreenChart!)}>
                      <FileSpreadsheet className="h-4 w-4" />
                      CSV (Dados)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportData("xlsx", fullscreenChart!)}>
                      <FileSpreadsheet className="h-4 w-4" />
                      Excel (XLSX)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportData("json", fullscreenChart!)}>
                      <Code className="h-4 w-4" />
                      JSON
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setFullscreenChart(null)}
                  aria-label="Fechar"
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-hidden p-5">
              <div className="h-full w-full">
                {fullscreenChart === "requests" && requestsChartData && (
                  <Bar
                    id="fullscreen-requests-chart"
                    key={`fullscreen-${requestsChartKey}`}
                    options={getFullscreenOptions(requestsOptions)}
                    data={requestsChartData}
                  />
                )}
                {fullscreenChart === "costs" && costsChartData && (
                  <Bar
                    id="fullscreen-costs-chart"
                    key={`fullscreen-${costsChartKey}`}
                    options={getFullscreenOptions(costsOptions)}
                    data={costsChartData}
                  />
                )}
                {fullscreenChart === "tokens" && tokensChartData && (
                  <Bar
                    id="fullscreen-tokens-chart"
                    key={`fullscreen-${tokensChartKey}`}
                    options={getFullscreenOptions(tokensOptions)}
                    data={tokensChartData}
                  />
                )}
                {fullscreenChart === "model-cost" && modelCostChartData && (
                  <Bar
                    id="fullscreen-model-cost-chart"
                    key={`fullscreen-${modelCostChartKey}`}
                    options={getFullscreenOptions(modelCostOptions)}
                    data={modelCostChartData}
                  />
                )}
                {fullscreenChart === "api-key-usage" && usageByApiKeyMemo.data && (
                  <Bar
                    id="fullscreen-api-key-usage-chart"
                    key={`fullscreen-${usageByApiKeyChartKey}`}
                    options={getFullscreenOptions(apiKeyUsageOptions)}
                    data={usageByApiKeyMemo.data}
                  />
                )}
                {fullscreenChart === "user-usage" && usageByUserMemo.data && (
                  <Bar
                    id="fullscreen-user-usage-chart"
                    key={`fullscreen-${usageByUserChartKey}`}
                    options={getFullscreenOptions(userUsageOptions)}
                    data={usageByUserMemo.data}
                  />
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {paymentStage !== "" && <PaymentModal />}
        <ToastContainer />
      </div>
    </TooltipProvider>
  );
}
